import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * Grace period between a pointer leaving an open item and the panel actually
 * collapsing. A hop to a sibling item (across the ~16px inter-item gap) re-enters
 * within this window and cancels the pending close, so the panel swaps content
 * instead of collapsing to height 0 and re-expanding — the visible flicker/
 * "closes by itself" when sweeping across the top-level items.
 */
const CLOSE_INTENT_DELAY_MS = 120;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#pointerExitWatcher?.abort();
    this.#pointerExitWatcher = null;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    // Treat any axis of motion as "moving". Without the abs on movementY, upward
    // moves (negative movementY) were misread as stationary, dropping the safety
    // box and firing an eager reconcile mid-motion — a hit-test that can hand the
    // pointer to a sibling item and close the open panel.
    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;

      // The open submenu is a full-width panel that overlaps the other top-level
      // items' hit areas. Reconciling from inside it would hand activation to
      // whichever sibling sits under the pointer and close the panel the user is
      // actually using, so treat "pointer is in the open submenu" as no-op.
      if (this.#pointerWithinActiveSubmenu(target)) return;

      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: true }));
      }
    });
  }

  /**
   * Whether the given hit-test target sits inside the currently open submenu.
   * @param {Element} target
   * @returns {boolean}
   */
  #pointerWithinActiveSubmenu(target) {
    const submenu = findSubmenu(this.#state.activeItem);
    return Boolean(submenu?.contains(target));
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    // Landing on (or back on) a menu item cancels a close queued by a brief
    // pointer-out, so hopping between panel items swaps rather than collapses.
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;

    if (item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    if (isDefaultSlot && !findSubmenu(item)) {
      this.#deactivate();
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Swapping straight to another item skips #deactivate, so reset the
      // outgoing panel's rails here to keep "reopen lands neutral" intact.
      resetRails(findSubmenu(previouslyActiveItem));
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Watches for the pointer genuinely exiting the active item after a spurious
   * pointerleave was ignored (see deactivate).
   * @type {AbortController | null}
   */
  #pointerExitWatcher = null;

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Chromium can fire a spurious pointerleave whose relatedTarget is still inside
    // the item when the submenu opens underneath a stationary pointer (e.g. resting
    // on the last pixel row of the header). Acting on it closes the submenu, which
    // puts the pointer back on the item and re-activates it — a rapid open/close
    // flicker. Ignore the event, but watch for the pointer genuinely leaving the
    // item, since the browser's hover state is corrupted at this point and won't
    // fire another pointerleave for this item.
    if (
      event.type === 'pointerleave' &&
      event.relatedTarget instanceof Node &&
      event.target.contains(event.relatedTarget)
    ) {
      this.#watchForPointerExit(event.target);
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    // `document.activeElement` only tracks focus, so it never reports a pointer
    // moving into the panel; check the pointer's destination as well.
    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      (menu?.contains(document.activeElement) || menu?.contains(event.relatedTarget));
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#scheduleClose(this.#state.activeItem);
  }

  /**
   * Queue a close after CLOSE_INTENT_DELAY_MS. Bound to the item that was active
   * when the pointer left, so if a swap re-points activeItem in the meantime the
   * deferred #deactivate no-ops (its item != current activeItem guard).
   * @param {HTMLElement | null} item
   */
  #scheduleClose(item) {
    if (!item) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#deactivate(item);
    }, CLOSE_INTENT_DELAY_MS);
  }

  /**
   * Deactivate the menu item once the pointer moves outside of the given list item.
   * Replaces the pointerleave event the browser fails to deliver after a spurious
   * leave corrupted its hover state (see deactivate).
   * @param {Element} listItem
   */
  #watchForPointerExit(listItem) {
    this.#pointerExitWatcher?.abort();
    const watcher = new AbortController();
    this.#pointerExitWatcher = watcher;

    const menuitem = this.#state.activeItem;

    document.addEventListener(
      'pointerover',
      (event) => {
        if (event.target instanceof Node && listItem.contains(event.target)) return;
        // Keep the menu open while the pointer is over the submenu itself.
        if (event.target instanceof Element && this.#pointerWithinActiveSubmenu(event.target)) return;

        watcher.abort();
        this.#pointerExitWatcher = null;
        // No-ops if another item was activated in the meantime
        this.#deactivate(menuitem);
      },
      { signal: watcher.signal }
    );
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;

    this.#pointerExitWatcher?.abort();
    this.#pointerExitWatcher = null;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      resetRails(submenu);
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Left-rail → detail swap for the sidebar mega menu.
 *
 * Hovering or focusing an L2 rail row reveals its matching L3 detail panel.
 * All detail panels are stacked in a single grid cell (see the stylesheet), so
 * the open panel's height is constant regardless of which category is active —
 * no need to re-measure the header's `--submenu-height`.
 *
 * @extends {Component}
 */
class MegaMenuRail extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#select(this.dataset.defaultCat || '');
  }

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-cat]') : null;
    if (!(trigger instanceof HTMLElement)) return;

    this.#select(trigger.dataset.cat);
  };

  /**
   * Clear the active category, so reopening the menu (or moving to another L1
   * item) lands on the neutral state — no detail panel, only the link's own
   * promo cards — instead of whichever row was hovered last.
   */
  reset() {
    this.#select(this.dataset.defaultCat || '');
  }

  /**
   * @param {string | undefined} cat - Empty string selects the no-category state:
   *   the aside group with an empty `data-cat` (the L1-wide promo cards).
   */
  #select(cat) {
    if (cat == null) return;

    for (const link of this.querySelectorAll('.mm-rail__link')) {
      link.classList.toggle('is-active', /** @type {HTMLElement} */ (link).dataset.cat === cat);
    }
    // Detail panels and the promo card groups swap together, so an open L2
    // category always replaces the level-1 cards rather than sitting beside them.
    for (const detail of this.querySelectorAll('.mm-detail, .mm-panel__aside-group')) {
      detail.classList.toggle('is-active', /** @type {HTMLElement} */ (detail).dataset.cat === cat);
    }
  }
}

if (!customElements.get('mega-menu-rail')) {
  customElements.define('mega-menu-rail', MegaMenuRail);
}

/**
 * Reset every sidebar rail inside a submenu to its neutral (no-category) state.
 * @param {HTMLElement | null} submenu
 */
function resetRails(submenu) {
  if (!submenu) return;
  for (const rail of submenu.querySelectorAll('mega-menu-rail')) {
    if (rail instanceof MegaMenuRail) rail.reset();
  }
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
